/** Executive overview tab - trend chart (waste cost/quantity/% of sales & purchases over time),
 * sites/categories requiring attention, and a summary of active waste reduction actions. */

import { Box, Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { ATTENTION_ITEMS, WASTE_TREND } from '../data';
import { WasteActionItem } from '../types';
import { StatusChip } from './StatusChip';

interface OverviewTabProps {
  currencyCode?: string;
  actions: WasteActionItem[];
  onNavigateToActions: () => void;
}

const CHART_HEIGHT = 260;

const OverviewTab = ({ currencyCode, actions, onNavigateToActions }: OverviewTabProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const activeActions = actions.filter((a) => a.status === 'Not started' || a.status === 'In progress' || a.status === 'Overdue');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardTrendTitle', 'Waste trend vs prior period')}
          </Typography>
          <Grid container
            spacing={4}>
            <Grid item
              xs={12}
              md={6}>
              <Typography variant="textSM"
                fontWeight={600}
                sx={{ mb: 1 }}>
                {t('wasteDashboardCostQuantity', 'Waste cost and quantity')}
              </Typography>
              <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
                <ResponsiveContainer>
                  <LineChart data={WASTE_TREND}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis yAxisId="cost" />
                    <YAxis yAxisId="qty"
                      orientation="right" />
                    <RechartsTooltip />
                    <Line yAxisId="cost"
                      type="monotone"
                      dataKey="wasteCost"
                      name={t('wasteDashboardWasteCost', 'Waste cost (\u00a3)')}
                      stroke={theme.palette.error.main}
                      strokeWidth={2} />
                    <Line yAxisId="qty"
                      type="monotone"
                      dataKey="wasteQuantityKg"
                      name={t('wasteDashboardWasteQuantity', 'Waste quantity (kg)')}
                      stroke={theme.palette.primary.main}
                      strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <Typography variant="textSM"
                fontWeight={600}
                sx={{ mb: 1 }}>
                {t('wasteDashboardPercentSalesPurchases', 'Waste as % of sales and purchases')}
              </Typography>
              <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
                <ResponsiveContainer>
                  <LineChart data={WASTE_TREND}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis unit="%" />
                    <RechartsTooltip />
                    <Line type="monotone"
                      dataKey="wastePercentOfSales"
                      name={t('wasteDashboardPercentOfSales', '% of sales')}
                      stroke={theme.palette.warning.main}
                      strokeWidth={2} />
                    <Line type="monotone"
                      dataKey="wastePercentOfPurchases"
                      name={t('wasteDashboardPercentOfPurchases', '% of purchases')}
                      stroke={theme.palette.error.main}
                      strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardAttentionTitle', 'Sites and categories requiring attention')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardScope', 'Scope')}</TableCell>
                <TableCell>{t('wasteDashboardName', 'Name')}</TableCell>
                <TableCell>{t('wasteDashboardRegion', 'Region')}</TableCell>
                <TableCell>{t('wasteDashboardMetric', 'Metric')}</TableCell>
                <TableCell>{t('wasteDashboardReason', 'Likely reason')}</TableCell>
                <TableCell>{t('wasteDashboardSeverity', 'Severity')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ATTENTION_ITEMS.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.scope}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.region}</TableCell>
                  <TableCell>{item.metric}</TableCell>
                  <TableCell>{item.reason}</TableCell>
                  <TableCell><StatusChip status={item.severity}
                    label={item.severity === 'critical' ? t('wasteDashboardCritical', 'Critical') : t('wasteDashboardWarning', 'Warning')} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section onClick={onNavigateToActions}
        style={{ cursor: 'pointer' }}>
        <Box sx={{ p: 4 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3 }}>
            <Typography variant="textMD"
              fontWeight={600}>
              {t('wasteDashboardActiveActionsTitle', 'Active waste reduction actions')}
            </Typography>
            <Chip label={t('wasteDashboardViewAll', 'View all actions')}
              size="small"
              color="primary"
              variant="outlined" />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardAction', 'Action')}</TableCell>
                <TableCell>{t('wasteDashboardOwner', 'Owner')}</TableCell>
                <TableCell>{t('wasteDashboardDueDate', 'Due date')}</TableCell>
                <TableCell align="right">{t('wasteDashboardEstimatedSaving', 'Estimated saving')}</TableCell>
                <TableCell>{t('wasteDashboardStatus', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeActions.map((action) => (
                <TableRow key={action.id}>
                  <TableCell>{action.action}</TableCell>
                  <TableCell>{action.owner}</TableCell>
                  <TableCell>{action.dueDate}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(action.estimatedSaving, currencyCode)}</TableCell>
                  <TableCell>
                    <StatusChip status={action.status === 'Overdue' ? 'critical' : action.priority}
                      label={action.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default OverviewTab;
